import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
// String.prototype.toTranslatedDisplay() — must be first
import './app/utils/i18nExtension'

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_LABEL || 'mybooks',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    // These DOM-mutation errors have no first-party stack frame in any observed event —
    // they're the well-documented result of a browser extension (Chrome's built-in
    // translate, ad blockers, etc.) mutating nodes React also manages mid-commit, not an
    // app bug. Filtering them out keeps the issue list signal-to-noise usable.
    //
    // "Logging out" / "Session expired" CanceledErrors come from AuthHelpers.ts's axios
    // interceptor, which deliberately rejects in-flight requests when a logout or
    // session-expiry is in progress (see markLoggingOut / the 401 handler). None of the
    // many fetchData() call sites across the app catch this on purpose — the component is
    // unmounting/navigating to /auth/login regardless, so there's nothing to show the user.
    // It's expected control flow, not a failure; ignoring it here (rather than threading a
    // try/catch through every caller) keeps this fix local to the one place that knows why.
    ignoreErrors: [
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'removeChild' on 'Node'",
      'Logging out',
      'Session expired',
    ],
  })
}
// Axios
import axios from 'axios'
import { Chart, registerables } from 'chart.js'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import 'bootstrap/dist/css/bootstrap.min.css'
// import 'bootstrap/dist/js/bootstrap.bundle.min.js';
// Apps
import { LangProvider } from './_myb/i18n/LangConfig'
import './_myb/assets/sass/style.react.scss'
import './_myb/assets/fonticon/fonticon.css'
import './_myb/assets/keenicons/duotone/style.css'
import './_myb/assets/keenicons/outline/style.css'
import './_myb/assets/keenicons/solid/style.css'
import 'font-awesome/css/font-awesome.min.css';

import dotenv from 'dotenv';
/**
 * TIP: Replace this style import with rtl styles to enable rtl mode
 *
 * import './_myb/assets/css/style.rtl.css'
 **/
import './_myb/assets/sass/style.scss'
import { AppRoutes } from './app/routing/AppRoutes'
import { AuthProvider, setupAxios } from './app/modules/auth'
import { AppProvider } from './app/modules/apps/AppContext'
import { TidioChat } from './app/modules/apps/chat/TidioChat'
import { warmApi } from './app/warmup'

/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */
/**
 * Inject Metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
setupAxios(axios)
Chart.register(...registerables)

// MBAI-505: fire-and-forget Lambda warmup as early as possible so the cold
// start happens during app boot. Axios requests wait on it (see warmup.ts), so
// the cold start is paid once instead of once per parallel first request.
void warmApi()


const queryClient = new QueryClient()
const container = document.getElementById('root')
if (container) {
  createRoot(container).render(
    <QueryClientProvider client={queryClient}>
      <LangProvider>
        <AppProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </AppProvider>
      </LangProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <TidioChat />
    </QueryClientProvider>
  )
}
